import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bug, ArrowLeft, CalendarDays, RefreshCw, ClipboardCopy, Info, Mail } from 'lucide-react';
import { getDashboardDates, getDashboardReport, sendBugReportEmail } from '../services/api';
import { ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import html2canvas from 'html2canvas';

const WORKFLOW_SECTIONS = [
  { key: 'Open', label: 'Open', color: '#1976d2', description: 'Defect assigned for investigation and resolution.' },
  { key: 'Blocked', label: 'Blocked', color: '#c62828', description: 'Progress is pending due to a dependency or external issue.' },
  { key: 'Monitoring', label: 'Monitoring', color: '#f57c00', description: 'Fix is validated and being observed for stability.' },
  { key: 'Ready for QA', label: 'Ready for QA', color: '#2e7d32', description: 'Fix is available for QA validation.' },
];

const PRIORITY_ORDER = { 'Blocker': 0, 'High': 1, 'Medium': 2, 'Low': 3, 'Unset': 4 };
const TASKEI_URL = 'https://taskei.amazon.dev/tasks';

/** Teams that always appear as rows in the team-milestone tables (even with zero counts).
 *  Shared by both the live TeamMilestoneTable and the copy-to-clipboard HTML builder so
 *  the two never drift out of sync. */
const KNOWN_TEAMS = ['PCAPS', 'PCAPS-ES', 'Product', 'NOVA', 'Hawkeye', 'PLUTUS', 'TOSHIBA', 'KRATOS', 'ODYSSEY', 'RIS', 'FOUNDATION', 'GLOBALPAYMENTS'];

/** Escape HTML special characters to prevent XSS */
function escapeHtml(str) {
  if (!str) return '';
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function today() { return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago' }).format(new Date()); }

/** Filter milestone value to only show M-values (M3, M4, etc.) and Base ELERA, exclude R-values */
function filterMilestone(value) {
  if (!value) return '—';
  const parts = value.split(',').map(s => s.trim()).filter(s => /^M\d+$/.test(s) || s.toLowerCase() === 'base elera');
  return parts.length > 0 ? parts.join(', ') : '—';
}

function BugTable({ bugs, workflowLabel, color, description, owner, nextAction }) {
  const [showInfo, setShowInfo] = useState(false);

  // Product team members (case-insensitive match on assigned field)
  const PRODUCT_MEMBERS = ['athul', 'tanay', 'gary', 'xyangg', 'tanshaha'];
  const getEffectiveTeam = (bug) => {
    const assigned = bug.assigned || '';
    const labels = (bug.labels || '').toUpperCase();
    const lowerAssigned = assigned.toLowerCase();

    if (PRODUCT_MEMBERS.some(m => lowerAssigned.includes(m)) || labels.includes('PRODUCT_OWNER')) {
      return 'Product';
    } else if (labels.includes('HAWKEYE')) {
      return 'Hawkeye';
    }
    let team = bug.teams || '—';
    // If TOSHIBA label is present but not already in teams, append it
    if (labels.includes('TOSHIBA') && !(team.toUpperCase().includes('TOSHIBA'))) {
      team = team === '—' ? 'TOSHIBA' : `${team}, TOSHIBA`;
    }
    return team;
  };

  return (
    <div className="bug-table-section">
      <div className="bug-table-header">
        <h3 className="bug-table-title" style={{ borderLeftColor: color }}>
          {workflowLabel} <span className="bug-table-count">({bugs?.length || 0})</span>
        </h3>
        <button className="bug-table-info-btn" onClick={() => setShowInfo(!showInfo)} title="Show workflow definition"><Info size={14} /></button>
      </div>
      {showInfo && (
        <div className="bug-table-info-panel">
          <div>{description}</div>
        </div>
      )}
      {(!bugs || bugs.length === 0) ? (
        <p className="bug-table-empty">No bugs in this workflow.</p>
      ) : (
        <div className="bug-table-wrapper">
          <table className="bug-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Priority</th>
                <th>Module</th>
                <th>Assigned</th>
                <th>Teams</th>
                <th>Found in Milestone</th>
                <th>Targeted Milestone</th>
                <th>Lead</th>
              </tr>
            </thead>
            <tbody>
              {bugs.map((bug) => (
                <tr key={bug.shortId} className={`bug-row bug-row-${(bug.priority || 'unset').toLowerCase()}`}>
                  <td className="bug-title-cell">
                    <a href={`${TASKEI_URL}/${bug.shortId}`} target="_blank" rel="noopener noreferrer">
                      {bug.title}
                    </a>
                  </td>
                  <td><span className={`bug-priority-badge priority-${(bug.priority || 'unset').toLowerCase()}`}>{bug.priority}</span></td>
                  <td>{bug.module || '—'}</td>
                  <td>{bug.assigned || '—'}</td>
                  <td>{getEffectiveTeam(bug)}</td>
                  <td>{filterMilestone(bug.affectedMilestone)}</td>
                  <td>{filterMilestone(bug.targetedRelease)}</td>
                  <td>{bug.leadDays ? `${bug.leadDays}d` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TeamMilestoneTable({ teams, milestones }) {
  // Team label UUIDs for Taskei filter URLs
  const TEAM_LABEL_IDS = {
    'NOVA': 'd6afbb5d-fa1d-495e-bd89-55a1c3fb76eb',
    'PCAPS': '36038744-db4c-4309-9a13-19b18367f7f6',
    'PCAPS-ES': '021f8af9-dd0a-400a-9bc7-ed9de3f29dfc',
    'GLOBALPAYMENTS': 'f7d2411c-fa8b-4470-8485-e655b854fac6',
    'HAWKEYE': 'b8d9db9c-cb58-413e-8a27-8606adb39614',
    'PLUTUS': '9ae5852f-978c-4430-8845-ca57fc1873c9',
    'KRATOS': '12e1cf4e-7d1e-4cab-ab23-4b9e498c44d5',
    'FOUNDATION': '90e96dac-3a7a-4aac-9705-1061b78abbeb',
    'ODYSSEY': '86c41df7-5439-4433-acc0-e5f480d3351e',
    'RIS': 'ca480c73-282d-4517-8985-2b2d10fa5362',
    'TOSHIBA': '4054b678-5b30-4413-a25b-520af56b9ed8',
    'PRODUCT': null, // Virtual team — no Taskei label
  };

  const MILESTONE_OPTION_IDS = {
    'M3': 'M3\\/Bmpoivc2OodhRb-7z4bUi',
    'M4': 'M4\\/LkOAHvcJvJSRJb-7z4bUi',
    'M5': 'M5\\/X1EbYvcavaSdRb-7z4bUi',
    'M6': 'M6\\/_hjApoRZoCLWPJAZ7W-A3',
    'Base ELERA': '"Base ELERA/maLPYEfklaWfeN-mJPFqD"',
  };

  const TASKEI_BASE = 'https://taskei.amazon.dev/rooms/b435d0c2-7e26-46ee-9826-0972c3e39a46/tasks?ss=8744de83-dc2e-4465-96cd-78fbd6a015c2&f=';
  const PRODUCT_OWNER_LABEL_ID = 'a82d0413-101d-4537-8632-725e073fc706';
  const PRODUCT_MEMBERS_ALIASES = ['athul', 'tanshaha', 'xyangg'];
  const NLL_LABEL_ID = '80f8c428-67ee-41f1-b0e6-8f40e71657cb';

  const encodeFilter = (filter) => filter
    .replace(/\\\//g, '%5C%2F')
    .replace(/"/g, '%22')
    .replace(/\//g, '%2F')
    .replace(/:/g, '%3A')
    .replace(/ /g, '+')
    .replace(/\(/g, '%28')
    .replace(/\)/g, '%29');

  const buildFilteredUrl = (team, milestone, priority, labelFilter) => {
    const msOption = milestone ? MILESTONE_OPTION_IDS[milestone] : null;
    const teamKey = team.toUpperCase();
    let filter;

    if (teamKey === 'PRODUCT') {
      const assigneeList = PRODUCT_MEMBERS_ALIASES.join(' OR ');
      filter = `NOT workflowAction:(Monitoring OR "Ready for QA" OR Deferred) AND (assignee:(${assigneeList}) OR labels:${PRODUCT_OWNER_LABEL_ID}) AND tags:APEX_BUG AND status:Open`;
    } else if (teamKey === 'HAWKEYE') {
      filter = `workflowAction:(Monitoring OR "Ready for QA") AND tags:APEX_BUG AND status:Open`;
    } else {
      const teamId = TEAM_LABEL_IDS[teamKey];
      if (!teamId) return null;
      filter = `NOT workflowAction:(Monitoring OR "Ready for QA" OR Deferred) AND labels:${teamId} AND tags:APEX_BUG AND status:Open`;
    }

    if (msOption) {
      filter += ` AND custom_attribute\\/MultiSelectCustomAttribute\\/affected_milestone_and_release:${msOption}`;
    }
    if (labelFilter === 'NLL') {
      filter += ` AND labels:${NLL_LABEL_ID}`;
    } else if (labelFilter === 'NOT_NLL') {
      filter += ` AND NOT labels:${NLL_LABEL_ID}`;
    }
    if (priority) {
      filter += ` AND classicPriority:${priority}`;
    }
    return `${TASKEI_BASE}${encodeFilter(filter)}`;
  };

  const CellLink = ({ url, count, color }) => {
    if (url && count > 0) {
      return <a href={url} target="_blank" rel="noopener noreferrer" style={{ color, textDecoration: 'underline', fontWeight: 600 }}>{count}</a>;
    }
    return <span style={{ color: count > 0 ? color : '#ccc', fontWeight: 600 }}>{count}</span>;
  };

  // Compute NLL and POST NLL totals
  const nllTotal = teams.reduce((sum, [, msData]) => sum + Object.values(msData).reduce((s, c) => s + (c.HighNll || 0) + (c.MediumNll || 0) + (c.LowNll || 0), 0), 0);
  const postNllTotal = teams.reduce((sum, [, msData]) => sum + Object.values(msData).reduce((s, c) => s + (c.High + c.Medium + c.Low) - (c.HighNll || 0) - (c.MediumNll || 0) - (c.LowNll || 0), 0), 0);

  const renderTable = (title, titleColor, isNll) => (
    <div style={{ marginBottom: '16px' }}>
      <h4 style={{ margin: '0 0 6px', fontSize: '13px', color: titleColor, fontWeight: 600, borderLeft: `4px solid ${titleColor}`, paddingLeft: '10px' }}>
        {title}
      </h4>
      <p style={{ margin: '0 0 6px', fontSize: '9px', color: '#999', paddingLeft: '14px', fontStyle: 'italic' }}>* Hawkeye row (Ready for QA / Monitoring) may overlap with team rows</p>
      <table className="bug-table" style={{ fontSize: '11px', tableLayout: 'fixed', width: '100%' }}>
        <thead>
          <tr>
            <th style={{ textAlign: 'left', borderBottom: '2px solid #ddd', width: '120px' }}>Team</th>
            {milestones.map(ms => (
              <th key={ms} style={{ textAlign: 'center', borderBottom: '2px solid #ddd', borderLeft: '1px solid #ddd' }}>{ms}</th>
            ))}
            <th style={{ textAlign: 'center', borderLeft: '2px solid #333', background: '#f5f5f5', borderBottom: '2px solid #ddd' }}>
              <div>Total</div>
              <div style={{ fontSize: '8px', fontWeight: 400, color: '#888', marginTop: '1px' }}>
                <span style={{ color: '#c62828' }}>H</span> / <span style={{ color: '#e65100' }}>M</span> / <span style={{ color: '#2e7d32' }}>L</span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {teams.map(([team, msData]) => {
            let teamTotal = 0;
            let teamHigh = 0;
            let teamMedium = 0;
            let teamLow = 0;

            // Compute totals regardless
            milestones.forEach(ms => {
              const c = msData[ms] || { High: 0, Medium: 0, Low: 0, HighNll: 0, MediumNll: 0, LowNll: 0 };
              let high, medium, low;
              if (isNll) { high = c.HighNll || 0; medium = c.MediumNll || 0; low = c.LowNll || 0; }
              else { high = c.High - (c.HighNll || 0); medium = c.Medium - (c.MediumNll || 0); low = c.Low - (c.LowNll || 0); }
              teamTotal += high + medium + low;
              teamHigh += high; teamMedium += medium; teamLow += low;
            });

            // Deferred row: just show total spanning all milestone columns
            if (team === 'Deferred') {
              if (teamTotal === 0) return null;
              return (
                <tr key={team} style={{ background: '#fafafa', borderTop: '2px solid #ddd' }}>
                  <td><strong style={{ color: '#757575' }}>{team}</strong></td>
                  <td colSpan={milestones.length} style={{ textAlign: 'center', borderLeft: '1px solid #ddd', color: '#757575', fontStyle: 'italic', fontSize: '10px' }}>—</td>
                  <td style={{ textAlign: 'center', borderLeft: '2px solid #333', background: '#f0f0f0', padding: '4px 6px' }}>
                    <div style={{ fontWeight: 700 }}>
                      <CellLink url={buildFilteredUrl(team, null, null, isNll ? 'NLL' : 'NOT_NLL')} count={teamTotal} color="#757575" />
                    </div>
                  </td>
                </tr>
              );
            }

            return (
              <tr key={team}>
                <td><strong>{team}</strong></td>
                {milestones.map(ms => {
                  const c = msData[ms] || { High: 0, Medium: 0, Low: 0, HighNll: 0, MediumNll: 0, LowNll: 0 };
                  let high, medium, low;
                  if (isNll) { high = c.HighNll || 0; medium = c.MediumNll || 0; low = c.LowNll || 0; }
                  else { high = c.High - (c.HighNll || 0); medium = c.Medium - (c.MediumNll || 0); low = c.Low - (c.LowNll || 0); }
                  const count = high + medium + low;
                  const url = buildFilteredUrl(team, ms, null, isNll ? 'NLL' : 'NOT_NLL');
                  return (
                    <td key={`${team}-${ms}`} style={{ textAlign: 'center', borderLeft: '1px solid #ddd' }}>
                      <CellLink url={url} count={count} color={isNll ? '#7b1fa2' : '#555'} />
                    </td>
                  );
                })}
                <td style={{ textAlign: 'center', borderLeft: '2px solid #333', background: '#f5f5f5', padding: '4px 6px' }}>
                  <div style={{ fontWeight: 700, marginBottom: '2px' }}>
                    <CellLink url={buildFilteredUrl(team, null, null, isNll ? 'NLL' : 'NOT_NLL')} count={teamTotal} color={isNll ? '#7b1fa2' : '#333'} />
                  </div>
                  <div style={{ fontSize: '9px', lineHeight: '1.3' }}>
                    <CellLink url={buildFilteredUrl(team, null, 'High', isNll ? 'NLL' : 'NOT_NLL')} count={teamHigh} color="#c62828" />
                    {' / '}
                    <CellLink url={buildFilteredUrl(team, null, 'Medium', isNll ? 'NLL' : 'NOT_NLL')} count={teamMedium} color="#e65100" />
                    {' / '}
                    <CellLink url={buildFilteredUrl(team, null, 'Low', isNll ? 'NLL' : 'NOT_NLL')} count={teamLow} color="#2e7d32" />
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div>
      {renderTable('NLL — Must Fix Before Launch', '#7b1fa2', true)}
      {renderTable('POST NLL — Targeted After Launch', '#555', false)}
    </div>
  );
}

function BugDashboard() {
  const navigate = useNavigate();
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(today());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailFeedback, setEmailFeedback] = useState(null);

  useEffect(() => {
    getDashboardDates().then(dates => {
      setAvailableDates(dates || []);
      if (dates && dates.length > 0 && !dates.includes(selectedDate)) {
        setSelectedDate(dates[0]);
      }
    });
  }, []);

  useEffect(() => {
    if (!selectedDate) return;
    setLoading(true);
    getDashboardReport(selectedDate, '72', '')
      .then(data => setReport(data))
      .catch(() => setReport(null))
      .finally(() => setLoading(false));
  }, [selectedDate, refreshKey]);

  const bugsByWorkflow = useMemo(() => {
    if (!report?.bugDetails) return {};
    const grouped = {};
    for (const wf of WORKFLOW_SECTIONS) {
      grouped[wf.key] = [];
    }
    grouped['Deferred'] = [];

    for (const bug of report.bugDetails) {
      const wf = bug.workflowStatus || 'Open';
      if (wf.toLowerCase() === 'deferred') {
        grouped['Deferred'].push(bug);
        continue;
      }
      const matchedSection = WORKFLOW_SECTIONS.find(s => s.key.toLowerCase() === wf.toLowerCase());
      if (matchedSection) {
        grouped[matchedSection.key].push(bug);
      } else {
        grouped['Open'].push(bug);
      }
    }

    for (const key of Object.keys(grouped)) {
      grouped[key].sort((a, b) => (PRIORITY_ORDER[a.priority] ?? 99) - (PRIORITY_ORDER[b.priority] ?? 99));
    }

    return grouped;
  }, [report]);

  const handleSendEmailReport = async () => {
    if (!report?.available) return;
    setSendingEmail(true);
    setEmailFeedback(null);
    try {
      const htmlBody = buildDashboardHtml();
      const allBugs = report.bugDetails || [];
      const result = await sendBugReportEmail(selectedDate, htmlBody, allBugs.length);
      setEmailFeedback({ type: 'success', message: `Report sent to ${result.recipients?.length || 0} recipient(s)` });
      setTimeout(() => setEmailFeedback(null), 5000);
    } catch (err) {
      const msg = err.message || 'Failed to send email report';
      setEmailFeedback({ type: 'error', message: msg });
      setTimeout(() => setEmailFeedback(null), 5000);
    } finally {
      setSendingEmail(false);
    }
  };

  /** Build the dashboard HTML — shared between copy-to-clipboard and send-email */
  const buildDashboardHtml = (chartImgHtml = '') => {
    const allBugs = report.bugDetails || [];

    const workflowTables = WORKFLOW_SECTIONS.map(wf => {
      const bugs = bugsByWorkflow[wf.key] || [];
      if (bugs.length === 0) return '';
      const rows = bugs.map(bug => {
        const link = `${TASKEI_URL}/${bug.shortId}`;
        const priorityColor = bug.priority === 'High' || bug.priority === 'Blocker' ? '#c62828' : bug.priority === 'Medium' ? '#e65100' : '#2e7d32';
        const foundIn = filterMilestone(bug.affectedMilestone);
        const target = filterMilestone(bug.targetedRelease);
        return `<tr>
          <td style="padding:6px 10px;border-bottom:1px solid #eee"><a href="${link}" style="color:#1565c0;text-decoration:none;font-weight:500">${escapeHtml(bug.title || bug.shortId)}</a></td>
          <td style="padding:6px 10px;border-bottom:1px solid #eee;color:${priorityColor};font-weight:600">${escapeHtml(bug.priority || 'Unset')}</td>
          <td style="padding:6px 10px;border-bottom:1px solid #eee">${escapeHtml(bug.module || '—')}</td>
          <td style="padding:6px 10px;border-bottom:1px solid #eee">${escapeHtml(bug.assigned || '—')}</td>
          <td style="padding:6px 10px;border-bottom:1px solid #eee">${escapeHtml(bug.teams || '—')}</td>
          <td style="padding:6px 10px;border-bottom:1px solid #eee">${escapeHtml(foundIn)}</td>
          <td style="padding:6px 10px;border-bottom:1px solid #eee">${escapeHtml(target)}</td>
          <td style="padding:6px 10px;border-bottom:1px solid #eee">${bug.leadDays ? bug.leadDays + 'd' : '—'}</td>
        </tr>`;
      }).join('');

      return `
        <h3 style="margin:20px 0 4px;padding-left:10px;border-left:4px solid ${wf.color};font-size:14px">${wf.label} (${bugs.length})</h3>
        <p style="margin:0 0 8px;font-size:11px;color:#666;padding-left:14px"><em>${wf.description}</em></p>
        <table style="width:100%;border-collapse:collapse;font-size:12px;margin-bottom:16px">
          <thead>
            <tr style="background:#f5f5f5">
              <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd">Title</th>
              <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd">Priority</th>
              <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd">Module</th>
              <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd">Assigned</th>
              <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd">Teams</th>
              <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd">Found in Milestone</th>
              <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd">Targeted Milestone</th>
              <th style="padding:6px 8px;text-align:left;border-bottom:2px solid #ddd">Lead</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      `;
    }).join('');

    const teamCounts = {};
    const PRODUCT_MEMBERS_EMAIL = ['athul', 'tanay', 'gary', 'xyangg', 'tanshaha'];
    const milestoneOrder = ['M3', 'M4', 'M5', 'Base ELERA'];

    // Build team × milestone × priority × NLL data for the HTML table
    const teamMsData = {};
    const allMilestonesSet = new Set();

    allBugs.filter(bug => !['Ready for QA', 'Monitoring', 'Deferred'].includes(bug.workflowStatus || 'Open')).forEach(bug => {
      const teams = (bug.teams || '').split(',').map(t => t.trim()).filter(Boolean);
      const prio = bug.priority || 'Unset';
      const labels = (bug.labels || '').toUpperCase();
      const assigned = (bug.assigned || '').toLowerCase();
      const isNll = labels.includes('NLL_MUST_HAVE');

      let targets;
      if (PRODUCT_MEMBERS_EMAIL.some(m => assigned.includes(m)) || labels.includes('PRODUCT_OWNER')) {
        targets = ['Product'];
      } else if (labels.includes('HAWKEYE')) {
        targets = ['Hawkeye'];
      } else {
        targets = teams.length > 0 ? teams : (labels.includes('TOSHIBA') ? ['TOSHIBA'] : ['Unassigned']);
      }

      const msRaw = bug.affectedMilestone || '';
      const msParts = msRaw.split(',').map(s => s.trim()).filter(s => /^M\d+$/i.test(s) || s.toLowerCase() === 'base elera');
      const milestones = msParts.length > 0 ? msParts.map(m => m.toLowerCase() === 'base elera' ? 'Base ELERA' : m.toUpperCase()) : ['Unassigned'];

      milestones.forEach(ms => allMilestonesSet.add(ms));

      targets.forEach(t => {
        if (!teamMsData[t]) teamMsData[t] = {};
        milestones.forEach(ms => {
          if (!teamMsData[t][ms]) teamMsData[t][ms] = { High: 0, Medium: 0, Low: 0, HighNll: 0, MediumNll: 0, LowNll: 0 };
          if (prio === 'High' || prio === 'Blocker') { teamMsData[t][ms].High++; if (isNll) teamMsData[t][ms].HighNll++; }
          else if (prio === 'Medium') { teamMsData[t][ms].Medium++; if (isNll) teamMsData[t][ms].MediumNll++; }
          else { teamMsData[t][ms].Low++; if (isNll) teamMsData[t][ms].LowNll++; }
        });

        // Also accumulate simple team totals
        if (!teamCounts[t]) teamCounts[t] = { High: 0, Medium: 0, Low: 0 };
        if (prio === 'High' || prio === 'Blocker') teamCounts[t].High++;
        else if (prio === 'Medium') teamCounts[t].Medium++;
        else teamCounts[t].Low++;
      });

      // Additionally count under TOSHIBA if bug has that label (skip if already primary)
      if (labels.includes('TOSHIBA') && !targets.includes('TOSHIBA')) {
        if (!teamMsData['TOSHIBA']) teamMsData['TOSHIBA'] = {};
        milestones.forEach(ms => {
          if (!teamMsData['TOSHIBA'][ms]) teamMsData['TOSHIBA'][ms] = { High: 0, Medium: 0, Low: 0, HighNll: 0, MediumNll: 0, LowNll: 0 };
          if (prio === 'High' || prio === 'Blocker') { teamMsData['TOSHIBA'][ms].High++; if (isNll) teamMsData['TOSHIBA'][ms].HighNll++; }
          else if (prio === 'Medium') { teamMsData['TOSHIBA'][ms].Medium++; if (isNll) teamMsData['TOSHIBA'][ms].MediumNll++; }
          else { teamMsData['TOSHIBA'][ms].Low++; if (isNll) teamMsData['TOSHIBA'][ms].LowNll++; }
        });
      }
    });

    // Also add Hawkeye from Ready for QA / Monitoring bugs
    allBugs.filter(bug => ['Ready for QA', 'Monitoring'].includes(bug.workflowStatus || 'Open') || (bug.labels || '').toUpperCase().includes('HAWKEYE')).forEach(bug => {
      const prio = bug.priority || 'Unset';
      const labels = (bug.labels || '').toUpperCase();
      const isNll = labels.includes('NLL_MUST_HAVE');
      const msRaw = bug.affectedMilestone || '';
      const msParts = msRaw.split(',').map(s => s.trim()).filter(s => /^M\d+$/i.test(s) || s.toLowerCase() === 'base elera');
      const milestones = msParts.length > 0 ? msParts.map(m => m.toLowerCase() === 'base elera' ? 'Base ELERA' : m.toUpperCase()) : ['Unassigned'];
      milestones.forEach(ms => allMilestonesSet.add(ms));

      if (!teamMsData['Hawkeye']) teamMsData['Hawkeye'] = {};
      milestones.forEach(ms => {
        if (!teamMsData['Hawkeye'][ms]) teamMsData['Hawkeye'][ms] = { High: 0, Medium: 0, Low: 0, HighNll: 0, MediumNll: 0, LowNll: 0 };
        if (prio === 'High' || prio === 'Blocker') { teamMsData['Hawkeye'][ms].High++; if (isNll) teamMsData['Hawkeye'][ms].HighNll++; }
        else if (prio === 'Medium') { teamMsData['Hawkeye'][ms].Medium++; if (isNll) teamMsData['Hawkeye'][ms].MediumNll++; }
        else { teamMsData['Hawkeye'][ms].Low++; if (isNll) teamMsData['Hawkeye'][ms].LowNll++; }
      });
    });

    // Add Deferred bugs as a separate row
    allBugs.filter(bug => (bug.workflowStatus || 'Open').toLowerCase() === 'deferred').forEach(bug => {
      const prio = bug.priority || 'Unset';
      const labels = (bug.labels || '').toUpperCase();
      const isNll = labels.includes('NLL_MUST_HAVE');
      const msRaw = bug.affectedMilestone || '';
      const msParts = msRaw.split(',').map(s => s.trim()).filter(s => /^M\d+$/i.test(s) || s.toLowerCase() === 'base elera');
      const milestones = msParts.length > 0 ? msParts.map(m => m.toLowerCase() === 'base elera' ? 'Base ELERA' : m.toUpperCase()) : ['Unassigned'];
      // Note: deferred bugs render as a single total row, not per-milestone, so we do NOT
      // add their milestones to allMilestonesSet (matches the live table — avoids an empty "Unassigned" column)
      if (!teamMsData['Deferred']) teamMsData['Deferred'] = {};
      milestones.forEach(ms => {
        if (!teamMsData['Deferred'][ms]) teamMsData['Deferred'][ms] = { High: 0, Medium: 0, Low: 0, HighNll: 0, MediumNll: 0, LowNll: 0 };
        if (prio === 'High' || prio === 'Blocker') { teamMsData['Deferred'][ms].High++; if (isNll) teamMsData['Deferred'][ms].HighNll++; }
        else if (prio === 'Medium') { teamMsData['Deferred'][ms].Medium++; if (isNll) teamMsData['Deferred'][ms].MediumNll++; }
        else { teamMsData['Deferred'][ms].Low++; if (isNll) teamMsData['Deferred'][ms].LowNll++; }
      });
    });

    const sortedMilestones = [...milestoneOrder.filter(m => allMilestonesSet.has(m)), ...[...allMilestonesSet].filter(m => !milestoneOrder.includes(m) && m !== 'Unassigned')];
    if (allMilestonesSet.has('Unassigned')) sortedMilestones.push('Unassigned');

    // Ensure all known teams always appear (even with 0 counts)
    KNOWN_TEAMS.forEach(team => { if (!teamMsData[team]) teamMsData[team] = {}; });

    const sortedTeamEntries = Object.entries(teamMsData).sort((a, b) => {
      if (a[0] === 'Deferred') return 1;
      if (b[0] === 'Deferred') return -1;
      const totalA = Object.values(a[1]).reduce((s, c) => s + c.High + c.Medium + c.Low, 0);
      const totalB = Object.values(b[1]).reduce((s, c) => s + c.High + c.Medium + c.Low, 0);
      return totalB - totalA;
    });

    // Build team-milestone HTML table with NLL breakdown
    const NLL_LABEL_ID_HTML = '80f8c428-67ee-41f1-b0e6-8f40e71657cb';
    const TASKEI_BASE_HTML = 'https://taskei.amazon.dev/rooms/b435d0c2-7e26-46ee-9826-0972c3e39a46/tasks?ss=8744de83-dc2e-4465-96cd-78fbd6a015c2&f=';
    const MILESTONE_OPTION_IDS_HTML = { 'M3': 'M3\\/Bmpoivc2OodhRb-7z4bUi', 'M4': 'M4\\/LkOAHvcJvJSRJb-7z4bUi', 'M5': 'M5\\/X1EbYvcavaSdRb-7z4bUi', 'M6': 'M6\\/_hjApoRZoCLWPJAZ7W-A3', 'Base ELERA': '"Base ELERA/maLPYEfklaWfeN-mJPFqD"' };
    const TEAM_LABEL_IDS_HTML = { 'NOVA': 'd6afbb5d-fa1d-495e-bd89-55a1c3fb76eb', 'PCAPS': '36038744-db4c-4309-9a13-19b18367f7f6', 'PCAPS-ES': '021f8af9-dd0a-400a-9bc7-ed9de3f29dfc', 'GLOBALPAYMENTS': 'f7d2411c-fa8b-4470-8485-e655b854fac6', 'HAWKEYE': 'b8d9db9c-cb58-413e-8a27-8606adb39614', 'PLUTUS': '9ae5852f-978c-4430-8845-ca57fc1873c9', 'KRATOS': '12e1cf4e-7d1e-4cab-ab23-4b9e498c44d5', 'FOUNDATION': '90e96dac-3a7a-4aac-9705-1061b78abbeb', 'ODYSSEY': '86c41df7-5439-4433-acc0-e5f480d3351e', 'RIS': 'ca480c73-282d-4517-8985-2b2d10fa5362', 'TOSHIBA': '4054b678-5b30-4413-a25b-520af56b9ed8' };
    const PRODUCT_OWNER_LABEL_HTML = 'a82d0413-101d-4537-8632-725e073fc706';

    const encodeFilter = (filter) => filter.replace(/\\\//g, '%5C%2F').replace(/"/g, '%22').replace(/\//g, '%2F').replace(/:/g, '%3A').replace(/ /g, '+').replace(/\(/g, '%28').replace(/\)/g, '%29');

    const buildHtmlCellUrl = (team, ms, priority, nllOnly) => {
      const msOption = ms ? MILESTONE_OPTION_IDS_HTML[ms] : null;
      const teamKey = team.toUpperCase();
      let filter;
      if (teamKey === 'PRODUCT') {
        filter = `NOT workflowAction:(Monitoring OR "Ready for QA" OR Deferred) AND (assignee:(athul OR tanshaha OR xyangg) OR labels:${PRODUCT_OWNER_LABEL_HTML}) AND tags:APEX_BUG AND status:Open`;
      } else if (teamKey === 'HAWKEYE') {
        filter = `workflowAction:(Monitoring OR "Ready for QA") AND tags:APEX_BUG AND status:Open`;
      } else {
        const teamId = TEAM_LABEL_IDS_HTML[teamKey];
        if (!teamId) return null;
        filter = `NOT workflowAction:(Monitoring OR "Ready for QA" OR Deferred) AND labels:${teamId} AND tags:APEX_BUG AND status:Open`;
      }
      if (msOption) filter += ` AND custom_attribute\\/MultiSelectCustomAttribute\\/affected_milestone_and_release:${msOption}`;
      if (nllOnly) filter += ` AND labels:${NLL_LABEL_ID_HTML}`;
      else filter += ` AND NOT labels:${NLL_LABEL_ID_HTML}`;
      if (priority) filter += ` AND classicPriority:${priority}`;
      return `${TASKEI_BASE_HTML}${encodeFilter(filter)}`;
    };

    const linkCell = (url, count, color) => {
      if (url && count > 0) return `<a href="${url}" target="_blank" rel="noopener noreferrer" style="color:${color};text-decoration:underline;font-weight:600">${count}</a>`;
      return `<span style="color:${color};font-weight:600">${count}</span>`;
    };

    const tmMsHeaders = sortedMilestones.map(ms => `<th style="text-align:center;border-bottom:2px solid #ddd;border-left:1px solid #ddd;padding:4px">${escapeHtml(ms)}</th>`).join('');

    const buildHtmlTable = (tableTitle, titleColor, isNllTable) => {
      const rows = sortedTeamEntries.map(([team, msData]) => {
        let teamTotal = 0, teamHigh = 0, teamMedium = 0, teamLow = 0;
        sortedMilestones.forEach(ms => {
          const c = msData[ms] || { High: 0, Medium: 0, Low: 0, HighNll: 0, MediumNll: 0, LowNll: 0 };
          let high, medium, low;
          if (isNllTable) { high = c.HighNll || 0; medium = c.MediumNll || 0; low = c.LowNll || 0; }
          else { high = c.High - (c.HighNll || 0); medium = c.Medium - (c.MediumNll || 0); low = c.Low - (c.LowNll || 0); }
          teamTotal += high + medium + low; teamHigh += high; teamMedium += medium; teamLow += low;
        });
        // Skip zero-total rows only for non-known teams (known teams always show, matching the live table)
        if (teamTotal === 0 && team !== 'Deferred' && !KNOWN_TEAMS.includes(team)) return '';

        // Deferred row: just total, no per-milestone
        if (team === 'Deferred') {
          if (teamTotal === 0) return '';
          return `<tr style="background:#fafafa;border-top:2px solid #ddd"><td style="padding:4px 8px;font-weight:600;color:#757575">${escapeHtml(team)}</td><td colspan="${sortedMilestones.length}" style="text-align:center;border-left:1px solid #ddd;color:#757575;font-style:italic;font-size:10px">—</td><td style="text-align:center;border-left:2px solid #333;background:#f0f0f0;padding:4px 6px;font-weight:700;color:#757575">${teamTotal}</td></tr>`;
        }

        const cells = sortedMilestones.map(ms => {
          const c = msData[ms] || { High: 0, Medium: 0, Low: 0, HighNll: 0, MediumNll: 0, LowNll: 0 };
          let high, medium, low;
          if (isNllTable) { high = c.HighNll || 0; medium = c.MediumNll || 0; low = c.LowNll || 0; }
          else { high = c.High - (c.HighNll || 0); medium = c.Medium - (c.MediumNll || 0); low = c.Low - (c.LowNll || 0); }
          const count = high + medium + low;
          const url = buildHtmlCellUrl(team, ms, null, isNllTable);
          return `<td style="text-align:center;border-left:1px solid #ddd;padding:3px">${linkCell(url, count, isNllTable ? '#7b1fa2' : '#555')}</td>`;
        }).join('');
        const totalUrl = buildHtmlCellUrl(team, null, null, isNllTable);
        const highUrl = buildHtmlCellUrl(team, null, 'High', isNllTable);
        const medUrl = buildHtmlCellUrl(team, null, 'Medium', isNllTable);
        const lowUrl = buildHtmlCellUrl(team, null, 'Low', isNllTable);
        return `<tr><td style="padding:4px 8px;border-bottom:1px solid #eee;font-weight:600">${escapeHtml(team)}</td>${cells}<td style="text-align:center;border-left:2px solid #333;background:#f5f5f5;padding:4px 6px"><div style="font-weight:700">${linkCell(totalUrl, teamTotal, isNllTable ? '#7b1fa2' : '#333')}</div><div style="font-size:9px">${linkCell(highUrl, teamHigh, '#c62828')} / ${linkCell(medUrl, teamMedium, '#e65100')} / ${linkCell(lowUrl, teamLow, '#2e7d32')}</div></td></tr>`;
      }).join('');

      return `
        <h4 style="margin:16px 0 6px;padding-left:10px;border-left:4px solid ${titleColor};font-size:13px;font-weight:600;color:${titleColor}">${tableTitle}</h4>
        <table style="width:100%;table-layout:fixed;border-collapse:collapse;font-size:11px;margin-bottom:12px">
          <thead>
            <tr style="background:#f5f5f5">
              <th style="text-align:left;border-bottom:2px solid #ddd;padding:4px 8px;width:120px">Team</th>
              ${tmMsHeaders}
              <th style="text-align:center;border-left:2px solid #333;background:#f5f5f5;border-bottom:2px solid #ddd;padding:4px">Total<br/><span style="font-size:8px;font-weight:400;color:#888"><span style="color:#c62828">H</span> / <span style="color:#e65100">M</span> / <span style="color:#2e7d32">L</span></span></th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      `;
    };

    const teamBugTotal = allBugs.filter(bug => !['Ready for QA', 'Monitoring', 'Deferred'].includes(bug.workflowStatus || 'Open')).length;

    // Overview summary numbers
    const high = allBugs.filter(b => b.priority === 'High' || b.priority === 'Blocker').length;
    const medium = allBugs.filter(b => b.priority === 'Medium').length;
    const low = allBugs.filter(b => b.priority === 'Low').length;
    const nllBugs = allBugs.filter(b => (b.labels || '').toUpperCase().includes('NLL_MUST_HAVE'));
    const nllHigh = nllBugs.filter(b => b.priority === 'High' || b.priority === 'Blocker').length;
    const nllMedium = nllBugs.filter(b => b.priority === 'Medium').length;
    const nllLow = nllBugs.filter(b => b.priority === 'Low').length;

    return `
      <div style="font-family:Arial,sans-serif;max-width:1400px;margin:0 auto;padding:20px">
        <h2 style="margin:0 0 4px;color:#333">Bug Dashboard Report</h2>
        <p style="color:#666;margin:0 0 16px">Date: ${selectedDate} | Total Open Bugs: ${allBugs.length}</p>

        <!-- Overview Tiles -->
        <table style="width:100%;border-collapse:collapse;font-size:12px;margin-bottom:20px">
          <tr>
            <td style="width:50%;padding:12px;border:1px solid #e0e0e0;border-radius:8px;text-align:center;vertical-align:top">
              <div style="font-size:13px;color:#666;margin-bottom:4px">Total Open Bugs</div>
              <div style="font-size:32px;font-weight:700;color:#333;margin-bottom:8px">${allBugs.length}</div>
              <span style="color:#c62828;font-weight:600">High: <a href="${TASKEI_BASE_HTML}${encodeURIComponent('classicPriority:High AND tags:APEX_BUG AND status:Open')}" target="_blank" rel="noopener noreferrer" style="color:#c62828;text-decoration:underline">${high}</a></span> &nbsp;|&nbsp; <span style="color:#e65100;font-weight:600">Med: <a href="${TASKEI_BASE_HTML}${encodeURIComponent('classicPriority:Medium AND tags:APEX_BUG AND status:Open')}" target="_blank" rel="noopener noreferrer" style="color:#e65100;text-decoration:underline">${medium}</a></span> &nbsp;|&nbsp; <span style="color:#2e7d32;font-weight:600">Low: <a href="${TASKEI_BASE_HTML}${encodeURIComponent('classicPriority:Low AND tags:APEX_BUG AND status:Open')}" target="_blank" rel="noopener noreferrer" style="color:#2e7d32;text-decoration:underline">${low}</a></span>
            </td>
            <td style="width:8px"></td>
            <td style="width:50%;padding:12px;border:1px solid #e0e0e0;border-radius:8px;text-align:center;vertical-align:top">
              <div style="font-size:13px;color:#666;margin-bottom:4px">NLL - Must Fix</div>
              <div style="font-size:32px;font-weight:700;color:#333;margin-bottom:8px">${nllBugs.length}</div>
              <span style="color:#c62828;font-weight:600">High: <a href="${TASKEI_BASE_HTML}${encodeURIComponent('labels:' + NLL_LABEL_ID_HTML + ' AND classicPriority:High AND tags:APEX_BUG AND status:Open')}" target="_blank" rel="noopener noreferrer" style="color:#c62828;text-decoration:underline">${nllHigh}</a></span> &nbsp;|&nbsp; <span style="color:#e65100;font-weight:600">Med: <a href="${TASKEI_BASE_HTML}${encodeURIComponent('labels:' + NLL_LABEL_ID_HTML + ' AND classicPriority:Medium AND tags:APEX_BUG AND status:Open')}" target="_blank" rel="noopener noreferrer" style="color:#e65100;text-decoration:underline">${nllMedium}</a></span> &nbsp;|&nbsp; <span style="color:#2e7d32;font-weight:600">Low: <a href="${TASKEI_BASE_HTML}${encodeURIComponent('labels:' + NLL_LABEL_ID_HTML + ' AND classicPriority:Low AND tags:APEX_BUG AND status:Open')}" target="_blank" rel="noopener noreferrer" style="color:#2e7d32;text-decoration:underline">${nllLow}</a></span>
            </td>
          </tr>
        </table>

        <!-- Team × Milestone Tables -->
        <p style="margin:0 0 4px;font-size:9px;color:#999;font-style:italic">* Hawkeye row (Ready for QA / Monitoring) may overlap with team rows</p>
        ${buildHtmlTable('NLL — Must Fix Before Launch', '#7b1fa2', true)}
        ${buildHtmlTable('POST NLL — Targeted After Launch', '#555', false)}

        <!-- Bug Snapshot Chart -->
        ${chartImgHtml || (() => {
          // Build daily NLL burndown table for clipboard (last 3 weeks)
          const nllOpenBugs = allBugs.filter(b => (b.labels || '').toUpperCase().includes('NLL_MUST_HAVE'));
          const nllClosedBugs = (report.closedBugs || []).filter(b => (b.labels || '').toUpperCase().includes('NLL_MUST_HAVE'));
          const endDate = new Date(selectedDate + 'T23:59:59');
          const startDate = new Date(endDate); startDate.setDate(startDate.getDate() - 20);
          const days = [];
          for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
            days.push(new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago' }).format(d));
          }
          const createdPerDay = {}; const closedPerDay = {};
          days.forEach(day => { createdPerDay[day] = 0; closedPerDay[day] = 0; });
          [...nllOpenBugs, ...nllClosedBugs].forEach(bug => { const c = (bug.createdAt || '').slice(0, 10); if (createdPerDay[c] !== undefined) createdPerDay[c]++; });
          nllClosedBugs.forEach(bug => { const c = (bug.closedAt || '').slice(0, 10); if (closedPerDay[c] !== undefined) closedPerDay[c]++; });
          const totalCreatedBefore = [...nllOpenBugs, ...nllClosedBugs].filter(b => (b.createdAt || '').slice(0, 10) < days[0]).length;
          const totalClosedBefore = nllClosedBugs.filter(b => (b.closedAt || '').slice(0, 10) < days[0]).length;
          let cumNet = totalCreatedBefore - totalClosedBefore;
          const rows = days.map(day => {
            cumNet += createdPerDay[day] - closedPerDay[day];
            return `<tr><td style="padding:4px 8px;border-bottom:1px solid #eee;font-size:11px">${day.slice(5)}</td><td style="text-align:center;padding:4px;border-bottom:1px solid #eee;color:#c62828;font-weight:600">${createdPerDay[day]}</td><td style="text-align:center;padding:4px;border-bottom:1px solid #eee;color:#2e7d32;font-weight:600">${closedPerDay[day]}</td><td style="text-align:center;padding:4px;border-bottom:1px solid #eee;color:#1565c0;font-weight:700">${cumNet}</td></tr>`;
          }).join('');
          return `<h3 style="margin:20px 0 4px;padding-left:10px;border-left:4px solid #1976d2;font-size:14px">Bug Snapshot — NLL Must Have (Daily, Last 3 Weeks)</h3><p style="margin:0 0 8px;font-size:11px;color:#666;padding-left:14px;font-style:italic">Daily created vs closed for NLL_MUST_HAVE bugs up to ${selectedDate}</p><table style="width:auto;border-collapse:collapse;font-size:11px;margin-bottom:24px"><thead><tr style="background:#f5f5f5"><th style="padding:4px 8px;text-align:left;border-bottom:2px solid #ddd">Date</th><th style="padding:4px 8px;text-align:center;border-bottom:2px solid #ddd;color:#c62828">Created</th><th style="padding:4px 8px;text-align:center;border-bottom:2px solid #ddd;color:#2e7d32">Resolved</th><th style="padding:4px 8px;text-align:center;border-bottom:2px solid #ddd;color:#1565c0">Net Open</th></tr></thead><tbody>${rows}</tbody></table>`;
        })()}

        ${workflowTables}
        <hr style="margin:24px 0;border:none;border-top:1px solid #eee" />
        <p style="font-size:11px;color:#999">This report was sent from the QA Test Case Review Portal.</p>
      </div>
    `;
  };

  const handleCopyToClipboard = async () => {
    if (!report?.available) return;
    setCopied(false);
    try {
      let chartImgHtml = '';
      const chartEl = document.getElementById('burndown-chart-container');
      if (chartEl) {
        try {
          const canvas = await html2canvas(chartEl, { backgroundColor: '#ffffff', scale: 2 });
          const dataUrl = canvas.toDataURL('image/png');
          chartImgHtml = `<h3 style="margin:20px 0 4px;padding-left:10px;border-left:4px solid #1976d2;font-size:14px">Bug Snapshot — NLL Must Have (Daily, Last 3 Weeks)</h3><p style="margin:0 0 8px;font-size:11px;color:#666;padding-left:14px;font-style:italic">Daily created vs closed for NLL_MUST_HAVE bugs up to ${selectedDate}</p><img src="${dataUrl}" style="max-width:100%;height:auto;border:1px solid #e0e0e0;border-radius:8px;margin-bottom:16px" />`;
        } catch (e) {
          console.warn('Chart capture failed, using table fallback:', e);
        }
      }
      const html = buildDashboardHtml(chartImgHtml);
      const blob = new Blob([html], { type: 'text/html' });
      await navigator.clipboard.write([new ClipboardItem({ 'text/html': blob })]);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    } catch (err) {
      console.error('Copy to clipboard failed:', err);
      alert('Failed to copy to clipboard. Your browser may not support this feature.');
    }
  };

  const totalOpen = report?.bugDetails?.length || 0;
  const minDate = availableDates.length ? availableDates[availableDates.length - 1] : undefined;

  return (
    <div className="bug-dashboard">
      <button className="btn btn-secondary btn-sm back-to-dashboards" onClick={() => navigate('/dashboards')}><ArrowLeft size={14} /> Back to Dashboards</button>
      <div className="bug-dashboard-header">
        <div>
          <h2><Bug size={22} /> Bug Dashboard</h2>
          <p className="bug-dashboard-subtitle">Open defects grouped by workflow status. Total: {totalOpen} bugs.</p>
          <div className="bug-dashboard-controls" style={{ marginTop: '12px' }}>
            <label className="tr-date"><CalendarDays size={16} /><input type="date" value={selectedDate} min={minDate} max={today()} onChange={(e) => setSelectedDate(e.target.value)} className="input" /></label>
            <button className="btn btn-secondary btn-sm" onClick={() => setRefreshKey(k => k + 1)} disabled={loading}><RefreshCw size={14} /> Refresh</button>
            <button className="btn btn-secondary btn-sm" onClick={handleCopyToClipboard} disabled={loading || !report?.available} title="Copy dashboard as HTML to clipboard"><ClipboardCopy size={14} /> {copied ? '✓ Copied!' : 'Copy to Clipboard'}</button>
            <button className="btn btn-secondary btn-sm" onClick={handleSendEmailReport} disabled={loading || !report?.available || sendingEmail} title="Email recipients are configured in the Settings page"><Mail size={14} /> {sendingEmail ? 'Sending...' : 'Send Email Report'}</button>
          </div>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', alignItems: 'flex-end' }}>
          <span style={{ color: '#555', fontWeight: 600 }}>Bug Triage Filters:</span>
          <a href="https://taskei.amazon.dev/rooms/b435d0c2-7e26-46ee-9826-0972c3e39a46/tasks?ss=8744de83-dc2e-4465-96cd-78fbd6a015c2&f=labels%3A80f8c428-67ee-41f1-b0e6-8f40e71657cb+AND+classicPriority%3AHigh+AND+tags%3AAPEX_BUG+AND+status%3AOpen&d=board" target="_blank" rel="noopener noreferrer" style={{ color: '#c62828', textDecoration: 'underline', fontWeight: 500 }}>NLL_MUST_HAVE - High</a>
          <a href="https://taskei.amazon.dev/rooms/b435d0c2-7e26-46ee-9826-0972c3e39a46/tasks?ss=8744de83-dc2e-4465-96cd-78fbd6a015c2&f=labels%3A80f8c428-67ee-41f1-b0e6-8f40e71657cb+AND+classicPriority%3AMedium+AND+tags%3AAPEX_BUG+AND+status%3AOpen&d=board" target="_blank" rel="noopener noreferrer" style={{ color: '#e65100', textDecoration: 'underline', fontWeight: 500 }}>NLL_MUST_HAVE - Medium</a>
        </div>
      </div>
      {emailFeedback && (
        <div className={`bug-dashboard-email-feedback ${emailFeedback.type === 'success' ? 'feedback-success' : 'feedback-error'}`} role="status">
          {emailFeedback.message}
        </div>
      )}

      {loading && <div className="bug-dashboard-loading">Loading bug data...</div>}

      {!loading && !report?.available && (
        <div className="bug-dashboard-placeholder"><p>No bug data available for {selectedDate}.</p></div>
      )}

      {!loading && report?.available && (
        <div className="bug-tables-container">
          {/* Summary Tiles */}
          <div className="bug-summary-tiles" style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
            {/* Total Open Bugs Tile */}
            {(() => {
              const bugs = report.bugDetails || [];
              const high = bugs.filter(b => b.priority === 'High' || b.priority === 'Blocker').length;
              const medium = bugs.filter(b => b.priority === 'Medium').length;
              const low = bugs.filter(b => b.priority === 'Low').length;
              const taskeiBase = 'https://taskei.amazon.dev/rooms/b435d0c2-7e26-46ee-9826-0972c3e39a46/tasks?ss=8744de83-dc2e-4465-96cd-78fbd6a015c2&f=';
              const buildUrl = (priority) => {
                return `${taskeiBase}${encodeURIComponent(`classicPriority:${priority} AND tags:APEX_BUG AND status:Open`)}`;
              };
              return (
                <div style={{ flex: '1', minWidth: '280px', background: '#fff', borderRadius: '8px', border: '1px solid #e0e0e0', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', textAlign: 'center' }}>
                  <h4 style={{ margin: '0 0 4px', fontSize: '13px', color: '#666', fontWeight: 500 }}>Total Open Bugs</h4>
                  <p style={{ margin: '0 0 8px', fontSize: '10px', color: '#999', fontStyle: 'italic' }}>NLL_MUST_HAVE + POST_NLL</p>
                  <div style={{ fontSize: '36px', fontWeight: 700, color: '#333', marginBottom: '12px' }}>{bugs.length}</div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', fontSize: '12px' }}>
                    <div style={{ textAlign: 'center' }}><div style={{ color: '#c62828', fontWeight: 600 }}>High</div><a href={buildUrl('High')} target="_blank" rel="noopener noreferrer" style={{ fontSize: '18px', fontWeight: 700, color: '#c62828', textDecoration: 'underline' }}>{high}</a></div>
                    <div style={{ textAlign: 'center' }}><div style={{ color: '#e65100', fontWeight: 600 }}>Medium</div><a href={buildUrl('Medium')} target="_blank" rel="noopener noreferrer" style={{ fontSize: '18px', fontWeight: 700, color: '#e65100', textDecoration: 'underline' }}>{medium}</a></div>
                    <div style={{ textAlign: 'center' }}><div style={{ color: '#2e7d32', fontWeight: 600 }}>Low</div><a href={buildUrl('Low')} target="_blank" rel="noopener noreferrer" style={{ fontSize: '18px', fontWeight: 700, color: '#2e7d32', textDecoration: 'underline' }}>{low}</a></div>
                  </div>
                </div>
              );
            })()}

            {/* NLL MUST HAVE Tile */}
            {(() => {
              const bugs = report.bugDetails || [];
              const nllBugs = bugs.filter(b => (b.labels || '').toUpperCase().includes('NLL_MUST_HAVE'));
              const nllHigh = nllBugs.filter(b => b.priority === 'High' || b.priority === 'Blocker').length;
              const nllMedium = nllBugs.filter(b => b.priority === 'Medium').length;
              const nllLow = nllBugs.filter(b => b.priority === 'Low').length;
              const taskeiBase = 'https://taskei.amazon.dev/rooms/b435d0c2-7e26-46ee-9826-0972c3e39a46/tasks?ss=8744de83-dc2e-4465-96cd-78fbd6a015c2&f=';
              const buildNllUrl = (priority) => {
                return `${taskeiBase}${encodeURIComponent(`labels:80f8c428-67ee-41f1-b0e6-8f40e71657cb AND classicPriority:${priority} AND tags:APEX_BUG AND status:Open`)}`;
              };
              return (
                <div style={{ flex: '1', minWidth: '280px', background: '#fff', borderRadius: '8px', border: '1px solid #e0e0e0', padding: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.08)', textAlign: 'center' }}>
                  <h4 style={{ margin: '0 0 2px', fontSize: '13px', color: '#666', fontWeight: 500 }}>NLL - Must Fix</h4>
                  <p style={{ margin: '0 0 8px', fontSize: '10px', color: '#999', fontStyle: 'italic' }}>Bugs that must be fixed before NLL launch</p>
                  <div style={{ fontSize: '36px', fontWeight: 700, color: '#333', marginBottom: '12px' }}>{nllBugs.length}</div>
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '24px', fontSize: '12px' }}>
                    <div style={{ textAlign: 'center' }}><div style={{ color: '#c62828', fontWeight: 600 }}>High</div><a href={buildNllUrl('High')} target="_blank" rel="noopener noreferrer" style={{ fontSize: '18px', fontWeight: 700, color: '#c62828', textDecoration: 'underline' }}>{nllHigh}</a></div>
                    <div style={{ textAlign: 'center' }}><div style={{ color: '#e65100', fontWeight: 600 }}>Medium</div><a href={buildNllUrl('Medium')} target="_blank" rel="noopener noreferrer" style={{ fontSize: '18px', fontWeight: 700, color: '#e65100', textDecoration: 'underline' }}>{nllMedium}</a></div>
                    <div style={{ textAlign: 'center' }}><div style={{ color: '#2e7d32', fontWeight: 600 }}>Low</div><a href={buildNllUrl('Low')} target="_blank" rel="noopener noreferrer" style={{ fontSize: '18px', fontWeight: 700, color: '#2e7d32', textDecoration: 'underline' }}>{nllLow}</a></div>
                  </div>
                </div>
              );
            })()}
          </div>

          {/* Team Summary Table by Milestone */}
          <div className="bug-table-section">
            <div className="bug-table-wrapper">
              {(() => {
                const allBugsForTable = report.bugDetails || [];
                const bugs = allBugsForTable.filter(bug => !['Ready for QA', 'Monitoring', 'Deferred'].includes(bug.workflowStatus || 'Open'));
                const deferredBugs = allBugsForTable.filter(bug => (bug.workflowStatus || 'Open') === 'Deferred');
                // Hawkeye also gets bugs with Ready for QA/Monitoring status or HAWKEYE label
                const hawkeyeBugs = allBugsForTable.filter(bug => {
                  const wfStatus = bug.workflowStatus || 'Open';
                  const labels = (bug.labels || '').toUpperCase();
                  return ['Ready for QA', 'Monitoring'].includes(wfStatus) || labels.includes('HAWKEYE');
                });
                const milestoneOrder = ['M3', 'M4', 'M5', 'Base ELERA'];

                // Product team members (case-insensitive match on assigned field)
                const PRODUCT_MEMBERS = ['athul', 'tanay', 'gary', 'xyangg', 'tanshaha'];
                const isProductMember = (assigned) => {
                  if (!assigned) return false;
                  const lower = assigned.toLowerCase();
                  return PRODUCT_MEMBERS.some(m => lower.includes(m));
                };

                // Build: { team: { milestone: { High: n, Medium: n, Low: n, HighNll: n, MediumNll: n, LowNll: n } } }
                const data = {};
                bugs.forEach(bug => {
                  const teams = (bug.teams || '').split(',').map(t => t.trim()).filter(Boolean);
                  const prio = bug.priority || 'Unset';
                  const labels = (bug.labels || '').toUpperCase();
                  const isNll = labels.includes('NLL_MUST_HAVE');

                  // Determine effective teams with override rules:
                  // 1. If assigned to Athul, Tanay, or Gary → Product team only
                  // 2. If has PRODUCT_OWNER label → Product team only
                  // 3. If no team but has TOSHIBA label → TOSHIBA as primary team
                  let targets;
                  if (isProductMember(bug.assigned) || labels.includes('PRODUCT_OWNER')) {
                    targets = ['Product'];
                  } else {
                    targets = teams.length > 0 ? teams : (labels.includes('TOSHIBA') ? ['TOSHIBA'] : ['Unassigned']);
                  }

                  const msRaw = bug.affectedMilestone || '';
                  const msParts = msRaw.split(',').map(s => s.trim()).filter(s => /^M\d+$/i.test(s) || s.toLowerCase() === 'base elera');
                  const milestones = msParts.length > 0 ? msParts.map(m => m.toLowerCase() === 'base elera' ? 'Base ELERA' : m.toUpperCase()) : ['Unassigned'];

                  targets.forEach(team => {
                    if (!data[team]) data[team] = {};
                    milestones.forEach(ms => {
                      if (!data[team][ms]) data[team][ms] = { High: 0, Medium: 0, Low: 0, HighNll: 0, MediumNll: 0, LowNll: 0 };
                      if (prio === 'High' || prio === 'Blocker') { data[team][ms].High++; if (isNll) data[team][ms].HighNll++; }
                      else if (prio === 'Medium') { data[team][ms].Medium++; if (isNll) data[team][ms].MediumNll++; }
                      else { data[team][ms].Low++; if (isNll) data[team][ms].LowNll++; }
                    });
                  });

                  // Additionally count under TOSHIBA if bug has that label (skip if already primary)
                  if (labels.includes('TOSHIBA') && !targets.includes('TOSHIBA')) {
                    if (!data['TOSHIBA']) data['TOSHIBA'] = {};
                    milestones.forEach(ms => {
                      if (!data['TOSHIBA'][ms]) data['TOSHIBA'][ms] = { High: 0, Medium: 0, Low: 0, HighNll: 0, MediumNll: 0, LowNll: 0 };
                      if (prio === 'High' || prio === 'Blocker') { data['TOSHIBA'][ms].High++; if (isNll) data['TOSHIBA'][ms].HighNll++; }
                      else if (prio === 'Medium') { data['TOSHIBA'][ms].Medium++; if (isNll) data['TOSHIBA'][ms].MediumNll++; }
                      else { data['TOSHIBA'][ms].Low++; if (isNll) data['TOSHIBA'][ms].LowNll++; }
                    });
                  }
                });

                // Add Hawkeye row from Ready for QA/Monitoring bugs + HAWKEYE-labeled bugs
                hawkeyeBugs.forEach(bug => {
                  const prio = bug.priority || 'Unset';
                  const labels = (bug.labels || '').toUpperCase();
                  const isNll = labels.includes('NLL_MUST_HAVE');
                  const msRaw = bug.affectedMilestone || '';
                  const msParts = msRaw.split(',').map(s => s.trim()).filter(s => /^M\d+$/i.test(s) || s.toLowerCase() === 'base elera');
                  const milestones = msParts.length > 0 ? msParts.map(m => m.toLowerCase() === 'base elera' ? 'Base ELERA' : m.toUpperCase()) : ['Unassigned'];

                  if (!data['Hawkeye']) data['Hawkeye'] = {};
                  milestones.forEach(ms => {
                    if (!data['Hawkeye'][ms]) data['Hawkeye'][ms] = { High: 0, Medium: 0, Low: 0, HighNll: 0, MediumNll: 0, LowNll: 0 };
                    if (prio === 'High' || prio === 'Blocker') { data['Hawkeye'][ms].High++; if (isNll) data['Hawkeye'][ms].HighNll++; }
                    else if (prio === 'Medium') { data['Hawkeye'][ms].Medium++; if (isNll) data['Hawkeye'][ms].MediumNll++; }
                    else { data['Hawkeye'][ms].Low++; if (isNll) data['Hawkeye'][ms].LowNll++; }
                  });
                });

                const allMilestones = [...new Set(Object.values(data).flatMap(m => Object.keys(m)))];
                const sortedMilestones = [...milestoneOrder.filter(m => allMilestones.includes(m)), ...allMilestones.filter(m => !milestoneOrder.includes(m) && m !== 'Unassigned')];
                if (allMilestones.includes('Unassigned')) sortedMilestones.push('Unassigned');

                // Ensure all known teams always appear (even with 0 counts)
                KNOWN_TEAMS.forEach(team => {
                  if (!data[team]) data[team] = {};
                });

                // Add Deferred bugs as a separate "Deferred" row
                if (!data['Deferred']) data['Deferred'] = {};
                deferredBugs.forEach(bug => {
                  const prio = bug.priority || 'Unset';
                  const labels = (bug.labels || '').toUpperCase();
                  const isNll = labels.includes('NLL_MUST_HAVE');
                  const msRaw = bug.affectedMilestone || '';
                  const msParts = msRaw.split(',').map(s => s.trim()).filter(s => /^M\d+$/i.test(s) || s.toLowerCase() === 'base elera');
                  const milestones = msParts.length > 0 ? msParts.map(m => m.toLowerCase() === 'base elera' ? 'Base ELERA' : m.toUpperCase()) : ['Unassigned'];
                  milestones.forEach(ms => {
                    if (!data['Deferred'][ms]) data['Deferred'][ms] = { High: 0, Medium: 0, Low: 0, HighNll: 0, MediumNll: 0, LowNll: 0 };
                    if (prio === 'High' || prio === 'Blocker') { data['Deferred'][ms].High++; if (isNll) data['Deferred'][ms].HighNll++; }
                    else if (prio === 'Medium') { data['Deferred'][ms].Medium++; if (isNll) data['Deferred'][ms].MediumNll++; }
                    else { data['Deferred'][ms].Low++; if (isNll) data['Deferred'][ms].LowNll++; }
                  });
                });

                const sortedTeams = Object.entries(data).sort((a, b) => {
                  // Deferred always last
                  if (a[0] === 'Deferred') return 1;
                  if (b[0] === 'Deferred') return -1;
                  const totalA = Object.values(a[1]).reduce((sum, c) => sum + c.High + c.Medium + c.Low, 0);
                  const totalB = Object.values(b[1]).reduce((sum, c) => sum + c.High + c.Medium + c.Low, 0);
                  return totalB - totalA;
                });

                return <TeamMilestoneTable teams={sortedTeams} milestones={sortedMilestones} />;
              })()}
            </div>
          </div>

          {/* Bug Snapshot Chart */}
          <div className="bug-table-section">
            <h3 className="bug-table-title" style={{ borderLeftColor: '#1976d2' }}>Bug Snapshot — NLL Must Have</h3>
            <p style={{ margin: '0 0 12px', fontSize: '11px', color: '#666', paddingLeft: '14px', fontStyle: 'italic' }}>Daily view for NLL_MUST_HAVE bugs (last 3 weeks up to {selectedDate})</p>
            <div id="burndown-chart-container" style={{ width: '100%', height: 380, background: '#fff', borderRadius: '8px', border: '1px solid #e0e0e0', padding: '16px' }}>
              {(() => {
                const allBugs = report.bugDetails || [];
                const closedBugs = report.closedBugs || [];

                // Filter to NLL_MUST_HAVE only
                const nllOpen = allBugs.filter(b => (b.labels || '').toUpperCase().includes('NLL_MUST_HAVE'));
                const nllClosed = closedBugs.filter(b => (b.labels || '').toUpperCase().includes('NLL_MUST_HAVE'));

                // Build date range: last 3 weeks ending on selectedDate
                const endDate = new Date(selectedDate + 'T23:59:59');
                const startDate = new Date(endDate);
                startDate.setDate(startDate.getDate() - 20); // 3 weeks = 21 days

                const days = [];
                for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
                  days.push(new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Chicago' }).format(d));
                }

                // Count bugs created per day and closed per day
                const createdPerDay = {};
                const closedPerDay = {};
                days.forEach(day => { createdPerDay[day] = 0; closedPerDay[day] = 0; });

                // All NLL bugs (open + closed) created dates
                [...nllOpen, ...nllClosed].forEach(bug => {
                  const created = (bug.createdAt || '').slice(0, 10);
                  if (createdPerDay[created] !== undefined) createdPerDay[created]++;
                });

                // Closed bugs: use closedAt date
                nllClosed.forEach(bug => {
                  const closed = (bug.closedAt || '').slice(0, 10);
                  if (closedPerDay[closed] !== undefined) closedPerDay[closed]++;
                });

                // Compute cumulative net open: total created up to date - total closed up to date
                // First get total created BEFORE our window
                const totalCreatedBefore = [...nllOpen, ...nllClosed].filter(b => (b.createdAt || '').slice(0, 10) < days[0]).length;
                const totalClosedBefore = nllClosed.filter(b => (b.closedAt || '').slice(0, 10) < days[0]).length;

                let cumNetOpen = totalCreatedBefore - totalClosedBefore;

                const chartData = days.map(day => {
                  cumNetOpen += createdPerDay[day] - closedPerDay[day];
                  return {
                    date: day.slice(5), // MM-DD format
                    'Created': createdPerDay[day],
                    'Resolved': closedPerDay[day],
                    'Net Open': cumNetOpen,
                  };
                });

                return (
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={{ top: 10, right: 50, left: 10, bottom: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                      <XAxis dataKey="date" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={50} />
                      <YAxis yAxisId="left" tick={{ fontSize: 11 }} label={{ value: 'Daily', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: '#888' } }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} stroke="#1565c0" label={{ value: 'Net Open', angle: 90, position: 'insideRight', style: { fontSize: 10, fill: '#1565c0' } }} />
                      <Tooltip contentStyle={{ fontSize: '12px' }} />
                      <Legend wrapperStyle={{ fontSize: '12px' }} />
                      <Bar yAxisId="left" dataKey="Created" fill="#ef5350" opacity={0.7} name="Created (daily)" />
                      <Line yAxisId="left" type="monotone" dataKey="Resolved" stroke="#2e7d32" strokeWidth={2} dot={{ r: 3 }} name="Resolved (daily)" />
                      <Line yAxisId="right" type="monotone" dataKey="Net Open" stroke="#1565c0" strokeWidth={2.5} dot={{ r: 3 }} name="Net Open (cumulative)" />
                    </ComposedChart>
                  </ResponsiveContainer>
                );
              })()}
            </div>
          </div>

          {WORKFLOW_SECTIONS.map(wf => (
            <BugTable
              key={wf.key}
              bugs={bugsByWorkflow[wf.key]}
              workflowLabel={wf.label}
              color={wf.color}
              description={wf.description}
              owner={wf.owner}
              nextAction={wf.nextAction}
            />
          ))}

        </div>
      )}
    </div>
  );
}

export default BugDashboard;
